import { StorageSerializers, useLocalStorage } from "@vueuse/core";
import type { LocationReference, LocationColor, LocationColorsResponse } from "~/types/api";

function getRandomColor(): string {
  const r = Math.floor(Math.random() * 256);
  const g = Math.floor(Math.random() * 256);
  const b = Math.floor(Math.random() * 256);
  return `rgb(${r}, ${g}, ${b})`;
}

const globalLocationColors = ref<Map<number, string>>(new Map());
const globalStandardColors = ref<string[]>([]);
const globalIsInitialized = ref(false);
const globalIsLoading = ref(false);

interface LocationColorsCache extends LocationColorsResponse {
  cachedAt: number;
}

const LOCATION_COLORS_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const locationColorsCache = useLocalStorage<LocationColorsCache | null>("location_colors_cache", null, {
  serializer: StorageSerializers.object,
});

function isCacheFresh(cache: LocationColorsCache | null): cache is LocationColorsCache {
  return cache !== null && Date.now() - cache.cachedAt < LOCATION_COLORS_CACHE_TTL_MS;
}

function applyLocationColorsResponse(response: LocationColorsResponse): void {
  response.locations.forEach((location: LocationColor) => {
    globalLocationColors.value.set(location.id, location.color);
  });

  globalStandardColors.value = response.standard_colors;
}

export const useLocationLegend = () => {
  const locationColorMap = ref<Map<number, string>>(new Map());
  const locationOrder = ref<LocationReference[]>([]);
  const isLoading = ref(false);
  const isInitialized = ref(false);

  const getLocationLegendBackgroundColor = (locationId: number, lightness = 0.85): string => {
    const color = getLocationLegendBorderColor(locationId);
    return lightenColor(color, lightness);
  };

  function lightenColor(color: string, lightness: number): string {
    const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (!rgbMatch) {
      return color;
    }

    const [, r = 0, g = 0, b = 0] = rgbMatch.map(Number);

    const lightenValue = (value: number) => {
      return Math.round(value + (255 - value) * lightness);
    };

    const newR = lightenValue(r);
    const newG = lightenValue(g);
    const newB = lightenValue(b);

    return `rgb(${newR}, ${newG}, ${newB})`;
  }

  const fetchLocationColors = async () => {
    isLoading.value = true;

    try {
      await initializeLocationColors();
      locationColorMap.value = new Map(globalLocationColors.value);
      isInitialized.value = true;
    } finally {
      isLoading.value = false;
    }
  };

  const addLocationToLegend = async (location: LocationReference | { id: number; name: string } | null | undefined) => {
    if (!location || !location.id || !location.name) {
      return;
    }

    if (!isInitialized.value) {
      await fetchLocationColors();
    }

    if (!locationOrder.value.find((loc) => loc.id === location.id)) {
      addLocationOrderToLegend(location as LocationReference);
      addLocationColorToLegend(location as LocationReference);
    }
  };

  const addLocationColorToLegend = (location: LocationReference) => {
    if (!locationColorMap.value.has(location.id)) {
      let color: string;

      const globalColor = globalLocationColors.value.get(location.id);
      if (globalColor) {
        color = globalColor;
      } else {
        const standardColor = globalStandardColors.value[locationColorMap.value.size];
        color = standardColor ?? getRandomColor();
      }

      locationColorMap.value.set(location.id, color);
    }
  };

  const addLocationOrderToLegend = (location: LocationReference) => {
    if (!locationOrder.value.find((loc) => loc.id === location.id)) {
      locationOrder.value.push(location);
    }
  };

  const getLocationLegendBorderColor = (locationId: number | undefined): string => {
    if (locationId === undefined || locationId === null) {
      console.warn("locationId is undefined, returning random color");
      return getRandomColor();
    }

    const localColor = locationColorMap.value.get(locationId);
    if (localColor) {
      return localColor;
    }

    const globalColor = globalLocationColors.value.get(locationId);
    if (globalColor) {
      locationColorMap.value.set(locationId, globalColor);
      return globalColor;
    }

    const fallbackColor = getRandomColor();
    locationColorMap.value.set(locationId, fallbackColor);
    return fallbackColor;
  };

  const getLocationLegendItems = () => {
    return locationOrder.value.map((location) => ({
      location,
      borderColor: getLocationLegendBorderColor(location.id),
    }));
  };

  return {
    getLocationLegendBorderColor,
    addLocationToLegend,
    getLocationLegendItems,
    getLocationLegendBackgroundColor,
    fetchLocationColors,
    isLoading,
  };
};

export const initializeLocationColors = async () => {
  if (globalIsInitialized.value) {
    return;
  }

  if (globalIsLoading.value) {
    while (globalIsLoading.value) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
    return;
  }

  if (isCacheFresh(locationColorsCache.value)) {
    applyLocationColorsResponse(locationColorsCache.value);
    globalIsInitialized.value = true;
    return;
  }

  globalIsLoading.value = true;

  try {
    const api = await useApi();
    const response = await api.getLocationColors();

    applyLocationColorsResponse(response);
    locationColorsCache.value = { ...response, cachedAt: Date.now() };

    globalIsInitialized.value = true;
  } catch (error) {
    console.error("Failed to fetch location colors in initializeLocationColors:", error);
    globalStandardColors.value = [
      "rgb(29, 78, 216)",
      "rgb(219, 39, 119)",
      "rgb(217, 119, 6)",
      "rgb(220, 38, 38)",
      "rgb(124, 58, 237)",
      "rgb(8, 145, 178)",
      "rgb(234, 88, 12)",
      "rgb(4, 120, 87)",
      "rgb(101, 163, 13)",
      "rgb(75, 85, 99)",
    ];
    globalIsInitialized.value = true;
  } finally {
    globalIsLoading.value = false;
  }
};

export const forgetLocationColors = () => {
  globalLocationColors.value = new Map();
  globalStandardColors.value = [];
  globalIsInitialized.value = false;
  globalIsLoading.value = false;
  locationColorsCache.value = null;
};

export const updateLocationColor = (locationId: number, color: string) => {
  globalLocationColors.value.set(locationId, color);

  if (locationColorsCache.value) {
    const locations = locationColorsCache.value.locations.map((location) =>
      location.id === locationId ? { ...location, color } : location,
    );

    locationColorsCache.value = { ...locationColorsCache.value, locations, cachedAt: Date.now() };
  }
};
